import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { ScanEye, Timer, CheckCheck, Cpu, MapPin, Globe, TrendingUp, Wifi, Users, HeartPulse, Footprints, Radar, ShieldAlert } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { Reveal } from "@/components/site/Reveal";
import { IMAGES } from "@/lib/images";

function useCountUp(target, active, duration = 1600) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf, start;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min((t - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.floor(eased * target));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [active, target, duration]);
  return val;
}

function useLiveMoney(start, euroPerSec = 63) {
  const [val, setVal] = useState(start);
  useEffect(() => {
    const inc = Math.max(1, Math.round(euroPerSec / 10));
    const id = setInterval(() => setVal((v) => v + inc), 100);
    return () => clearInterval(id);
  }, [start, euroPerSec]);
  return val;
}

const DETECT_ICONS = [Users, HeartPulse, Footprints, Radar, ShieldAlert];

export default function Impact() {
  const { t, lang } = useLang();
  const im = t.impact;
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const locale = lang === "de" ? "de-DE" : "it-IT";

  const germany = useCountUp(im.germany.value, inView);
  const world = useCountUp(im.world.value, inView);
  const money = useLiveMoney(im.saved.start);
  const nf = (n) => n.toLocaleString(locale);

  const counters = [
    { icon: MapPin, value: nf(germany), label: im.germany.label, accent: "#00E5FF" },
    { icon: Globe, value: nf(world), label: im.world.label, accent: "#80F2FF" },
    { icon: TrendingUp, value: `${im.saved.prefix}${nf(money)}`, label: im.saved.label, accent: "#FF5A36", live: true },
  ];

  const saraIcons = [Timer, ScanEye, CheckCheck];

  return (
    <section id="impact" ref={ref} className="relative py-24 md:py-32 border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-brand mb-8">{im.overline}</p>
        </Reveal>

        {/* Compact counters */}
        <div className="grid sm:grid-cols-3 gap-4">
          {counters.map((c, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div
                data-testid={`counter-${i}`}
                className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-ink-surface/50 px-5 py-4 hover:border-white/20 transition-colors duration-300"
              >
                <span className="shrink-0 grid place-items-center w-11 h-11 rounded-xl" style={{ background: `${c.accent}18`, color: c.accent }}>
                  <c.icon size={18} strokeWidth={1.8} />
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-white tabular-nums leading-none" style={{ color: c.live ? c.accent : "#fff" }}>
                      {c.value}
                    </span>
                    {c.live && (
                      <span className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest text-coral-soft">
                        <span className="w-1.5 h-1.5 rounded-full bg-coral-brand animate-pulse" /> live
                      </span>
                    )}
                  </div>
                  <span className="block mt-1.5 text-white/55 text-[13px] leading-snug">{c.label}</span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* S.A.R.A — camera control */}
        <Reveal>
          <div className="mt-6 relative rounded-3xl border border-white/10 overflow-hidden">
            <div className="absolute inset-0">
              <img src={IMAGES.saraKitchen} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-25" />
              <div className="absolute inset-0 bg-gradient-to-r from-ink-bg via-ink-bg/90 to-ink-bg/50" />
              <div className="absolute inset-0 aurora opacity-40 mix-blend-screen" />
            </div>
            <div className="relative grid lg:grid-cols-[1.5fr_1fr] gap-10 p-8 md:p-12">
              <div>
                <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-brand border border-cyan-brand/30 rounded-full px-3 py-1.5">
                  <Cpu size={13} /> {im.sara.tag}
                </span>
                <h3 className="mt-6 font-display font-black tracking-tighter text-white text-4xl md:text-6xl flex items-baseline gap-3">
                  {im.sara.name}
                  <span className="w-2.5 h-2.5 rounded-full bg-coral-brand animate-pulse" />
                </h3>
                <p className="mt-4 font-display text-lg md:text-2xl text-white/90 leading-tight max-w-2xl">{im.sara.title}</p>
                <p className="mt-4 text-white/60 leading-relaxed max-w-2xl">{im.sara.body}</p>
              </div>
              <div className="flex flex-col justify-center gap-3">
                {im.sara.points.map((p, i) => {
                  const Icon = saraIcons[i] || ScanEye;
                  return (
                    <div key={i} className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-sm">
                      <span className="shrink-0 grid place-items-center w-9 h-9 rounded-xl bg-cyan-brand/15 text-cyan-brand">
                        <Icon size={16} />
                      </span>
                      <p className="text-white/85 text-sm leading-snug">{p}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Reveal>

        {/* Sara Sensing — WiFi through-wall */}
        <Reveal>
          <div className="mt-6 relative rounded-3xl border border-white/10 overflow-hidden grid lg:grid-cols-2">
            <div className="relative min-h-[280px] lg:min-h-full">
              <img src={IMAGES.saraSensing} alt="Sara Sensing — rilevamento WiFi" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-ink-bg/90 lg:to-ink-bg" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-bg/80 via-transparent to-transparent lg:hidden" />
              <span className="absolute top-5 left-5 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-brand bg-ink-bg/60 backdrop-blur-md border border-cyan-brand/30 rounded-full px-3 py-1.5">
                <Wifi size={13} /> {im.sensing.tag}
              </span>
            </div>

            <div className="relative p-8 md:p-12">
              <h3 className="font-display font-black tracking-tighter text-white text-3xl md:text-5xl leading-[0.95]">{im.sensing.title}</h3>
              <p className="mt-5 text-white/65 leading-relaxed">{im.sensing.body}</p>

              <div className="mt-8 grid sm:grid-cols-2 gap-3">
                {im.sensing.detects.map((d, i) => {
                  const Icon = DETECT_ICONS[i] || Radar;
                  const isLast = i === im.sensing.detects.length - 1;
                  return (
                    <div key={i} className={`flex items-start gap-3 rounded-2xl border p-4 transition-colors duration-300 ${isLast ? "sm:col-span-2 border-coral-brand/30 bg-coral-brand/[0.06]" : "border-white/10 bg-white/[0.03]"}`}>
                      <span className={`shrink-0 grid place-items-center w-9 h-9 rounded-xl ${isLast ? "bg-coral-brand/20 text-coral-soft" : "bg-cyan-brand/15 text-cyan-brand"}`}>
                        <Icon size={16} />
                      </span>
                      <div>
                        <p className="text-white text-sm font-semibold leading-tight">{d.t}</p>
                        <p className="text-white/55 text-[13px] leading-snug mt-0.5">{d.d}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
